import { useState, type ReactNode } from 'react'
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type ColumnDef } from '@tanstack/react-table'
import { MoreVertical, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { ComboboxField } from '@/components/ui/combobox'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EditableField } from '@/components/ui/editable-field'
import { EmailLink } from '@/components/ui/email-link'
import { Input } from '@/components/ui/input'
import { PhoneLink } from '@/components/ui/phone-link'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { AccountDetailHeader } from '@/components/accounts/account-detail-header'
import { AccountTypeBadge, TickerBadge } from '@/components/accounts/account-badge'
import { ContactForm } from '@/components/contacts/contact-form'
import {
  useAccount,
  useAccounts,
  useUpdateAccount,
  useDeactivateAccount,
  useReactivateAccount,
} from '@/hooks/use-accounts'
import { useAccountDivisions, useCreateDivision, useDeleteDivision } from '@/hooks/use-account-divisions'
import { useContacts } from '@/hooks/use-contacts'
import { useCapabilities } from '@/hooks/use-capabilities'
import { normalizeUppercase, relativeTime } from '@/lib/utils'
import type { Database } from '@/types/database'

type AccountType = Database['public']['Enums']['account_type']
type AccountUpdate = Database['public']['Tables']['accounts']['Update']
type MarketSegment = Database['public']['Enums']['market_segment']
type ContactRow = NonNullable<ReturnType<typeof useContacts>['data']>[number]

const SUB_TYPE_LABELS: Record<string, string> = {
  direct: 'Direct',
  through_distribution: 'Thru Distribution',
  both: 'Both',
}

const SEGMENT_CONFIG: Record<string, { fg: string; bg: string }> = {
  SMB:        { fg: 'var(--status-won-fg)',       bg: 'var(--status-won-bg)' },
  Enterprise: { fg: 'var(--status-quoted-fg)',    bg: 'var(--status-quoted-bg)' },
  Government: { fg: 'var(--status-on-hold-fg)',   bg: 'var(--status-on-hold-bg)' },
  Education:  { fg: 'var(--status-pre-quote-fg)', bg: 'var(--status-pre-quote-bg)' },
}

const CONTACT_TYPE_LABELS: Record<string, string> = {
  reseller_rep: 'Reseller Rep',
  end_user_contact: 'End User Contact',
  distributor_rep: 'Distributor Rep',
}

const detailSearch = z.object({
  tab: z.enum(['overview', 'contacts', 'divisions']).optional().default('overview'),
})

export const Route = createFileRoute('/_authed/accounts/$accountId')({
  validateSearch: detailSearch,
  component: AccountDetailPage,
})

type AccountDetail = NonNullable<ReturnType<typeof useAccount>['data']>

function AccountDetailPage() {
  const { accountId } = Route.useParams()
  const { tab } = useSearch({ from: '/_authed/accounts/$accountId' })
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: account, isLoading, error } = useAccount(accountId)
  const { canManageDivisions } = useCapabilities()

  const deactivate = useDeactivateAccount(accountId)
  const reactivate = useReactivateAccount(accountId)

  const [markInactiveOpen, setMarkInactiveOpen] = useState(false)
  const [inactiveReason, setInactiveReason] = useState('')

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !account) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>
            Account not found.{' '}
            <Link to="/accounts" className="underline">Back to accounts</Link>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const isActive = account.is_active

  const actionMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreVertical size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isActive ? (
          <DropdownMenuItem
            onClick={() => {
              setInactiveReason('')
              setMarkInactiveOpen(true)
            }}
          >
            Mark inactive
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => void reactivate.mutateAsync()}
          >
            Reactivate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div className="flex flex-col h-full">
      <AccountDetailHeader account={account} actions={actionMenu} />
      <Tabs
        value={tab}
        onValueChange={(v) => void navigate({ to: '/accounts/$accountId', params: { accountId }, search: () => ({ tab: v as typeof tab }) })}
        className="flex-1 flex flex-col overflow-hidden"
      >
        <div className="border-b shrink-0">
          <TabsList className="h-10 w-full justify-start rounded-none bg-transparent p-0 gap-0 px-6">
            {(['overview', 'contacts', 'divisions'] as const).map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                className="capitalize rounded-none border-b-2 border-transparent px-4 h-10 data-[state=active]:border-[color:var(--accent-text)] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                {t}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex-1 overflow-auto">
          <TabsContent value="overview" className="mt-0 p-6">
            <OverviewContent account={account} accountId={accountId} isActive={isActive} />
          </TabsContent>
          <TabsContent value="contacts" className="mt-0 p-6">
            <ContactsContent accountId={accountId} isActive={isActive} />
          </TabsContent>
          <TabsContent value="divisions" className="mt-0 p-6">
            <DivisionsContent accountId={accountId} canManageDivisions={canManageDivisions} isActive={isActive} />
          </TabsContent>
        </div>
      </Tabs>

      {/* Mark inactive confirm */}
      <AlertDialog open={markInactiveOpen} onOpenChange={setMarkInactiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {account.name} as inactive?</AlertDialogTitle>
            <AlertDialogDescription>
              The account will be hidden from the default account list. You can
              reactivate it at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 pb-2">
            <Textarea
              placeholder="Reason (optional)"
              value={inactiveReason}
              onChange={(e) => setInactiveReason(e.target.value)}
              rows={2}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void deactivate.mutateAsync(inactiveReason || undefined)
                setMarkInactiveOpen(false)
              }}
            >
              Mark inactive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-32 shrink-0 pt-1 text-[13px] text-[color:var(--ink-3)]">{label}</span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}

function OverviewContent({
  account,
  accountId,
  isActive,
}: {
  account: AccountDetail
  accountId: string
  isActive: boolean
}) {
  const update = useUpdateAccount(accountId)
  const { data: accounts = [] } = useAccounts()
  const { canEditRestrictedFields } = useCapabilities()
  const [tickerPending, setTickerPending] = useState<{
    oldVal: string; newVal: string; resolve: (ok: boolean) => void
  } | null>(null)

  const save = async (field: keyof AccountUpdate, value: unknown) => {
    await update.mutateAsync({ [field]: value } as AccountUpdate)
    toast.success('Saved')
  }

  const parentOptions = accounts
    .filter((a) => a.id !== accountId)
    .map((a) => ({ value: a.id, label: a.name }))

  return (
    <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
          <h3 className="text-[14px] font-[560] leading-5 tracking-[-0.005em] text-[color:var(--ink)] whitespace-nowrap shrink-0">Identity</h3>
          <div className="flex-1 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
        </div>

        <FieldRow label="Name">
          <EditableField<string>
            value={account.name}
            onSave={async (v) => save('name', v)}
            display={(v) => <span className="text-[13px] font-medium">{v || '—'}</span>}
            edit={(v, set) => <Input value={v} onChange={(e) => set(e.target.value)} />}
            validate={(v) => (!v.trim() ? 'Name is required' : undefined)}
            disabled={!isActive}
          />
        </FieldRow>

        <FieldRow label="Customer Ref">
          <EditableField<string>
            value={account.customer_ref ?? ''}
            onSave={async (v) => save('customer_ref', normalizeUppercase(v) || null)}
            display={(v) => <span className="font-mono text-[13px]">{v || '—'}</span>}
            edit={(v, set) => (
              <Input
                value={v}
                onChange={(e) => set(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="Customer Ref"
              />
            )}
            adminOnly
            disabled={!isActive}
          />
        </FieldRow>

        <FieldRow label="Type">
          <EditableField<string>
            value={account.type}
            onSave={async (v) => save('type', v)}
            display={(v) => <AccountTypeBadge type={v as AccountType} />}
            edit={(v, set) => (
              <Select value={v} onValueChange={set}>
                <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reseller">Reseller</SelectItem>
                  <SelectItem value="distributor">Distributor</SelectItem>
                  <SelectItem value="end_user">End User</SelectItem>
                </SelectContent>
              </Select>
            )}
            disabled={!isActive}
          />
        </FieldRow>

        {account.type === 'reseller' && (
          <FieldRow label="Sub-type">
            <EditableField<string>
              value={account.sub_type ?? ''}
              onSave={async (v) => save('sub_type', v || null)}
              display={(v) => <span className="text-[13px]">{v ? (SUB_TYPE_LABELS[v] ?? v) : '—'}</span>}
              edit={(v, set) => (
                <Select value={v} onValueChange={set}>
                  <SelectTrigger className="h-8"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct</SelectItem>
                    <SelectItem value="through_distribution">Thru Distribution</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              )}
              disabled={!isActive}
            />
          </FieldRow>
        )}

        <FieldRow label="Parent account">
          <ComboboxField
            options={parentOptions}
            value={account.parent_account_id || undefined}
            onSave={async (v) => save('parent_account_id', v ?? null)}
            display={(v) => {
              const p = accounts.find((a) => a.id === v)
              return p ? (
                <Link
                  to="/accounts/$accountId"
                  params={{ accountId: p.id }}
                  className="text-[13px] text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {p.name}
                </Link>
              ) : (
                <span className="text-[13px] text-[color:var(--ink-3)]">—</span>
              )
            }}
            allowClear
            disabled={!isActive}
          />
        </FieldRow>

        <FieldRow label="Ticker">
          <EditableField<string>
            value={account.ticker_symbol ?? ''}
            onSave={async (v) => save('ticker_symbol', v.toUpperCase() || null)}
            onBeforeSave={async (next, current) => {
              if (next === current) return true
              return new Promise<boolean>((resolve) => {
                setTickerPending({ oldVal: current, newVal: next, resolve })
              })
            }}
            display={(v) =>
              v ? <TickerBadge ticker={v} /> : <span className="text-[13px] text-[color:var(--ink-3)]">—</span>
            }
            edit={(v, set) => (
              <Input
                value={v}
                onChange={(e) => set(e.target.value.toUpperCase())}
                placeholder="e.g. CNXN"
                maxLength={5}
                className="w-24"
              />
            )}
            disabled={!isActive}
          />
        </FieldRow>

        <FieldRow label="Can buy direct">
          <div className="flex items-center gap-2 py-1">
            <Checkbox
              checked={account.can_buy_direct}
              disabled={!canEditRestrictedFields || !isActive}
              onCheckedChange={canEditRestrictedFields && isActive ? (v) => void save('can_buy_direct', Boolean(v)) : undefined}
              className={(!canEditRestrictedFields || !isActive) ? 'opacity-50' : undefined}
            />
            <span className={`text-[13px]${(!canEditRestrictedFields || !isActive) ? ' opacity-50' : ''}`}>
              {account.can_buy_direct ? 'Yes' : 'No'}
            </span>
          </div>
        </FieldRow>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
          <h3 className="text-[14px] font-[560] leading-5 tracking-[-0.005em] text-[color:var(--ink)] whitespace-nowrap shrink-0">Contact info</h3>
          <div className="flex-1 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
        </div>

        <FieldRow label="Website">
          <EditableField<string>
            value={account.website ?? ''}
            onSave={async (v) => {
              const url = v ? (v.startsWith('http') ? v : `https://${v}`) : null
              await save('website', url)
            }}
            display={(v) =>
              v ? (
                <a
                  href={v}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {v}
                </a>
              ) : (
                <span className="text-[13px] text-[color:var(--ink-3)]">—</span>
              )
            }
            edit={(v, set) => (
              <Input value={v} onChange={(e) => set(e.target.value)} placeholder="https://…" />
            )}
            disabled={!isActive}
          />
        </FieldRow>

        <FieldRow label="Phone">
          <EditableField<string>
            value={account.phone ?? ''}
            onSave={async (v) => save('phone', v || null)}
            display={(v) => <PhoneLink number={v} />}
            edit={(v, set) => <Input value={v} onChange={(e) => set(e.target.value)} type="tel" />}
            disabled={!isActive}
          />
        </FieldRow>

        <FieldRow label="Address">
          <div className="space-y-1">
            <EditableField<string>
              value={account.address_line_1 ?? ''}
              onSave={async (v) => save('address_line_1', v || null)}
              display={(v) => <span className="text-[13px]">{v || '—'}</span>}
              edit={(v, set) => (
                <Input value={v} onChange={(e) => set(e.target.value)} placeholder="Address line 1" />
              )}
              disabled={!isActive}
            />
            <EditableField<string>
              value={account.address_line_2 ?? ''}
              onSave={async (v) => save('address_line_2', v || null)}
              display={(v) =>
                v ? <span className="text-[13px]">{v}</span> : <span className="text-xs text-[color:var(--ink-3)] italic">Add line 2…</span>
              }
              edit={(v, set) => (
                <Input value={v} onChange={(e) => set(e.target.value)} placeholder="Address line 2" />
              )}
              disabled={!isActive}
            />
          </div>
        </FieldRow>

        <div className="space-y-2">
          <FieldRow label="City">
            <EditableField<string>
              value={account.city ?? ''}
              onSave={async (v) => save('city', v || null)}
              display={(v) => <span className="text-[13px]">{v || '—'}</span>}
              edit={(v, set) => <Input value={v} onChange={(e) => set(e.target.value)} />}
              disabled={!isActive}
            />
          </FieldRow>
          <FieldRow label="State">
            <EditableField<string>
              value={account.state ?? ''}
              onSave={async (v) => save('state', v || null)}
              display={(v) => <span className="text-[13px]">{v || '—'}</span>}
              edit={(v, set) => <Input value={v} onChange={(e) => set(e.target.value)} />}
              disabled={!isActive}
            />
          </FieldRow>
          <FieldRow label="ZIP">
            <EditableField<string>
              value={account.zip ?? ''}
              onSave={async (v) => save('zip', v || null)}
              display={(v) => <span className="text-[13px]">{v || '—'}</span>}
              edit={(v, set) => <Input value={v} onChange={(e) => set(e.target.value)} />}
              disabled={!isActive}
            />
          </FieldRow>
        </div>

        <FieldRow label="Country">
          <EditableField<string>
            value={account.country ?? ''}
            onSave={async (v) => save('country', v || null)}
            display={(v) => <span className="text-[13px]">{v || '—'}</span>}
            edit={(v, set) => <Input value={v} onChange={(e) => set(e.target.value)} />}
            disabled={!isActive}
          />
        </FieldRow>
      </div>

      <div className="md:col-span-2 border-t pt-3 flex gap-6 text-xs text-[color:var(--ink-3)]">
        <span>Created {new Date(account.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        <span>Updated {relativeTime(account.updated_at)}</span>
      </div>
    </div>

    <AlertDialog open={tickerPending !== null}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm ticker symbol change</AlertDialogTitle>
          <AlertDialogDescription>
            Change ticker symbol from{' '}
            <strong className="text-foreground">{tickerPending?.oldVal || '(none)'}</strong> to{' '}
            <strong className="text-foreground">{tickerPending?.newVal || '(none)'}</strong>?
            This affects how this account matches to public stock data.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => { tickerPending?.resolve(false); setTickerPending(null) }}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => { tickerPending?.resolve(true); setTickerPending(null) }}>
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}

function ContactsContent({ accountId, isActive }: { accountId: string; isActive: boolean }) {
  const [formOpen, setFormOpen] = useState(false)
  const navigate = useNavigate()
  const { data: contacts = [], isLoading } = useContacts(accountId)

  const columns: ColumnDef<ContactRow>[] = [
    {
      id: 'name',
      accessorFn: (row) => `${row.last_name} ${row.first_name}`,
      header: ({ column }) => <SortableHeader label="Name" column={column} />,
      cell: ({ row }) => (
        <span className="font-medium">{row.original.first_name} {row.original.last_name}</span>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => <span className="text-[13px]">{row.original.title ?? '—'}</span>,
    },
    {
      id: 'division',
      header: 'Division',
      cell: ({ row }) => {
        const div = (row.original as ContactRow & { division?: { name: string } | null }).division
        return <span className="text-[13px]">{div?.name ?? '—'}</span>
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <EmailLink email={row.original.email} iconOnly />,
    },
    {
      accessorKey: 'contact_type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-normal text-xs">
          {CONTACT_TYPE_LABELS[row.original.contact_type] ?? row.original.contact_type}
        </Badge>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-6 h-[1px] bg-[color:var(--line)] shrink-0" aria-hidden="true" />
        <h3 className="text-[14px] font-[560] leading-5 tracking-[-0.005em] whitespace-nowrap shrink-0">Contacts ({contacts.length})</h3>
        <div className="flex-1 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
        {isActive && (
          <Button size="sm" onClick={() => setFormOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Add contact
          </Button>
        )}
      </div>
      <DataTable
        columns={columns}
        data={contacts}
        loading={isLoading}
        onRowClick={(row) =>
          void navigate({ to: '/contacts/$contactId', params: { contactId: row.id } })
        }
        emptyMessage="No contacts yet."
      />
      {isActive && (
        <ContactForm
          open={formOpen}
          onOpenChange={setFormOpen}
          defaultAccountId={accountId}
        />
      )}
    </div>
  )
}

function DivisionsContent({
  accountId,
  canManageDivisions,
  isActive,
}: {
  accountId: string
  canManageDivisions: boolean
  isActive: boolean
}) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newSegment, setNewSegment] = useState('')
  const navigate = useNavigate()
  const { data: divisions = [], isLoading } = useAccountDivisions(accountId)
  const createDivision = useCreateDivision(accountId)
  const deleteDivision = useDeleteDivision(accountId)
  const { data: contacts = [] } = useContacts(accountId)

  const contactsByDivision = contacts.reduce<Record<string, number>>((acc, c) => {
    if (c.division_id) acc[c.division_id] = (acc[c.division_id] ?? 0) + 1
    return acc
  }, {})

  const handleAdd = async () => {
    if (!newName.trim()) return
    await createDivision.mutateAsync({
      name: newName.trim(),
      market_segment: (newSegment as MarketSegment) || null,
    })
    setNewName('')
    setNewSegment('')
    setShowAddForm(false)
  }

  if (isLoading) return <Skeleton className="h-24 w-full" />

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-6 h-[1px] bg-[color:var(--line)] shrink-0" aria-hidden="true" />
        <h3 className="text-[14px] font-[560] leading-5 tracking-[-0.005em] whitespace-nowrap shrink-0">Divisions ({divisions.length})</h3>
        <div className="flex-1 h-[1px] bg-[color:var(--line)]" aria-hidden="true" />
        {isActive && (
          <Button size="sm" onClick={() => setShowAddForm(!showAddForm)}>
            <Plus className="mr-1 h-4 w-4" />
            Add division
          </Button>
        )}
      </div>

      {isActive && showAddForm && (
        <form
          onSubmit={(e) => { e.preventDefault(); void handleAdd() }}
          className="flex items-end gap-2 p-3 border border-[color:var(--line)] rounded-lg bg-[color:var(--surface-2)]"
        >
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Division name"
            className="max-w-xs"
            autoFocus
          />
          <Select value={newSegment || '__none__'} onValueChange={(v) => setNewSegment(v === '__none__' ? '' : v)}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Segment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              <SelectItem value="SMB">SMB</SelectItem>
              <SelectItem value="Enterprise">Enterprise</SelectItem>
              <SelectItem value="Government">Government</SelectItem>
              <SelectItem value="Education">Education</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" size="sm" disabled={!newName.trim() || createDivision.isPending}>Add</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
        </form>
      )}

      {divisions.length === 0 ? (
        <p className="text-[13px] text-[color:var(--ink-3)]">No divisions yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {divisions.map((div) => (
            <Card
              key={div.id}
              className="cursor-pointer hover:bg-[color:var(--surface-2)] transition-colors"
              onClick={() =>
                void navigate({
                  to: '/contacts',
                  search: { account_id: accountId, division_id: div.id },
                })
              }
            >
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-[13px] font-medium">{div.name}</CardTitle>
                  {canManageDivisions && isActive && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 text-[color:var(--ink-3)] hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); void deleteDivision.mutate(div.id) }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-1">
                {div.market_segment && (() => {
                  const seg = SEGMENT_CONFIG[div.market_segment]
                  return (
                    <Badge
                      variant="secondary"
                      className="text-xs font-normal"
                      style={seg ? { color: seg.fg, backgroundColor: seg.bg, borderColor: 'transparent' } : undefined}
                    >
                      {div.market_segment}
                    </Badge>
                  )
                })()}
                <p className="text-xs text-[color:var(--ink-3)]">
                  {contactsByDivision[div.id] ?? 0}{' '}
                  contact{(contactsByDivision[div.id] ?? 0) !== 1 ? 's' : ''}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
